import HeaderText from "modules/1-on-1s/components/HeaderText/HeaderText";
import React, { Dispatch, useEffect, useMemo, useState } from "react";
import CustomSearchIcon from "ReactSvgIcons/CustomSearchIcon";
import { Column, Edge, EntityListProps } from "../constants";
import { getListEntities } from "data/dataStudio.data";
import { finalize } from "rxjs";
import { Form } from "@themesberg/react-bootstrap";

interface DataStudioFilterDropdownProps {
  position?: "fixed" | "relative" | "static";
  dropdownPositionTop?: string;
  dropdownPositionLeft?: string;
  column: Column;
  selectedTableColumns?: Column[];
  setSelectedColumns: Dispatch<React.SetStateAction<Column[]>>;
  parentId?: string;
  setActiveFilterDropDownId: Dispatch<React.SetStateAction<string>>;
}

const DataStudioFilterDropdown: React.FC<DataStudioFilterDropdownProps> = ({
  position,
  dropdownPositionTop,
  dropdownPositionLeft,
  column,
  selectedTableColumns,
  setSelectedColumns,
  parentId,
  setActiveFilterDropDownId,
}) => {
  const [loading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [entityList, setEntityList] = useState<EntityListProps[]>([]);
  const [selectedSet, setSelectedSet] = useState<EntityListProps[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    const subscription = getListEntities((column as Edge)?.table ?? "")
      .pipe(
        finalize(() => {
          setIsLoading(false);
        })
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response?.data?.data || [];
          setEntityList(responseData);
          setSelectedSet((column as Edge)?.filters || []);
          setSelectAll(
            responseData.length === (column as Edge)?.filters?.length
          );
        },
        error: (error) => {
          console.log(error);
        },
      });
    return () => subscription.unsubscribe();
  }, []);

  const handleSelect = (value: EntityListProps) => {
    const item = selectedSet.find((val) => val.id === value.id);
    const newSelectedSet = item
      ? selectedSet.filter((val) => val.id !== value.id)
      : [...selectedSet, value];

    setSelectedSet(newSelectedSet);
    setSelectAll(newSelectedSet.length === entityList.length);
  };
  const handleSelectAll = () => {
    setSelectAll((prev) => {
      const newSelectAll = !prev;
      setSelectedSet(newSelectAll ? filteredEntities : []);
      return newSelectAll;
    });
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setSelectedSet([]);
    setSelectAll(false);
  };

  const filteredEntities = useMemo(() => {
    return entityList.filter((item) => {
      if (item.name) {
        return (
          item.name.toLowerCase().includes(searchQuery) ||
          item.id.toLowerCase().includes(searchQuery)
        );
      }
      return item.id.toLowerCase().includes(searchQuery); // Only search by ID if name is missing
    });
  }, [searchQuery, entityList]);

  const updateEntityById = (
    data: any[],
    targetId: string,
    filters: any
  ): any[] => {
    return data.map((item) => {
      if (item.id === targetId) {
        return { ...item, filters };
      }
      if (item.child) {
        return {
          ...item,
          child: updateEntityById([item.child], targetId, filters)[0],
        };
      }
      return item;
    });
  };

  const handleApply = () => {
    if (column.id) {
      setSelectedColumns((prevData) => {
        const updatedValue = updateEntityById(
          prevData,
          column.id as string,
          Array.from(selectedSet)
        );
        setActiveFilterDropDownId("");
        return updatedValue;
      });
    }
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{
        position: position,
        top: dropdownPositionTop,
        left: dropdownPositionLeft,
        zIndex: 1000,
        width: "300px",
        height: "300px",
        border: "1px solid #E6E6E7",
        borderRadius: "8px",
        padding: "10px 12px",
        backgroundColor: "white",
        boxShadow:
          "0px 0px 12px 0px rgba(0, 0, 0, 0.08), 0px 4px 32px 0px rgba(0, 0, 0, 0.08)",
      }}
      className="d-flex flex-column DataStudioFilterDropdown"
    >
      <div className="d-flex align-items-center justify-content-between">
        <HeaderText text="Filters" fontSize="16px" />
        <button
          style={{
            height: "20px",
            fontSize: "12px",
          }}
          className="btn btn-primary d-flex align-items-center justify-content-center"
          disabled={loading}
          onClick={handleClearAll}
        >
          Clear All
        </button>
      </div>
      <div
        style={{
          border: "1px solid #C2C3C5",
          borderRadius: "8px",
        }}
        className="d-flex mt-3 align-items-center justify-content-center gap-2 px-1"
      >
        <input
          style={{
            border: "none",
            borderRadius: "8px",
            height: "28px",
            outline: "none",
          }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={loading}
          placeholder="Search..."
          className="w-100 data-studio-input-class"
        />
        <CustomSearchIcon
          width={16}
          height={16}
          color="#C2C3C5"
          style={{
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
          }}
        />
      </div>

      <div className="mt-2 w-100 h-100 px-1 flex flex-column data-studio-scrollable-list">
        {loading && <div className="data-studio-loading-text">Loading...</div>}
        {!loading && (
          <>
            <div onClick={handleSelectAll} className="data-studio-text-class">
              <Form.Check type="checkbox" checked={selectAll} readOnly />
              Select All
            </div>
            {filteredEntities?.map((item, index) => (
              <div
                onClick={() => handleSelect(item)}
                className={`data-studio-entity-item ${
                  !!selectedSet.find((val) => val.id === item.id)
                    ? "data-studio-entity-item-selected"
                    : ""
                }`}
                title={item?.name || item?.id}
                key={index}
              >
                <Form.Check
                  type="checkbox"
                  readOnly
                  checked={Array.from(selectedSet).some(
                    (val) => val.id === item.id
                  )}
                />
                {item?.name || item?.id}
              </div>
            ))}
          </>
        )}
      </div>
      <div className="mt-3 d-flex align-items-center justify-content-end">
        <button
          style={{
            height: "20px",
            fontSize: "12px",
          }}
          onClick={handleApply}
          disabled={loading}
          className="btn btn-primary d-flex align-items-center justify-content-center"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default DataStudioFilterDropdown;
